import { SolutionScenario } from '@/types/scenarios/solution';
import { Cog, Phone, Calendar, Users, Wrench, AlertTriangle, Bell, ClipboardList, Truck, HardHat } from 'lucide-react';

export const mining: SolutionScenario = {
  meta: {
    id: 'mining',
    slug: 'mining',
    name: 'Mining',
    tagline: 'Genies that handle breakdowns, parts, and safety intake every shift',
    description: "A genie that triages breakdowns, captures safety reports, and handles parts and consumables calls across every shift and every site so your supervisors aren't the help desk.",
    market: 'Mining',
    subMarkets: [
      'Mine Operators',
      'Mining Equipment & OEMs',
      'Drilling & Blasting Contractors',
      'Exploration & Geology',
      'Consumables & Supplies',
      'Mine Site Services',
      'Haulage & Logistics'
    ],
    specificNeeds: [
      '24/7 breakdown & downtime response',
      'Parts and consumables sourcing',
      'Shift & contractor call-out coordination',
      'Safety incident and near-miss intake',
      'Equipment service & shutdown scheduling',
      'Remote site after hours support'
    ],
    commonRoles: ['Mine Supervisors', 'Maintenance Crews', 'Safety Officers', 'Procurement Teams', 'Shift Coordinators', 'Drillers'],
    marketSize: '$2.1 trillion globally',
    isFeatured: true
  },

  hero: {
    headline: 'Mine ready, every shift',
    headlineGradient: 'Mine ready,',
    headlineRest: 'every shift',
    subheadline: "A genie that triages breakdowns, captures safety reports, and handles parts and consumables calls across every shift and every site so your supervisors aren't the help desk.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'interactive-faq',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'breakdown-response',
      name: 'Breakdown & Downtime Response',
      description: 'Critical 24/7 response for equipment failures at the face, on the haul road, or in the plant',
      department: 'Maintenance',
      icon: AlertTriangle,
      playbookIds: ['interactive-faq', 'lead-capture'],
      benefits: [
        '24/7 breakdown triage',
        'Severity-based prioritisation',
        'Technician and parts dispatch',
        'Reduce unplanned downtime'
      ]
    },
    {
      id: 'parts-consumables',
      name: 'Parts & Consumables Ordering',
      description: 'Help crews reorder wear parts, bits, liners, belts, and consumables — even without a part number',
      department: 'Parts',
      icon: Cog,
      playbookIds: ['interactive-faq', 'lead-capture'],
      benefits: [
        'Cross-reference by equipment model',
        'Stock availability checks',
        '24/7 order placement',
        'Site-delivery coordination'
      ]
    },
    {
      id: 'service-shutdowns',
      name: 'Service & Shutdown Scheduling',
      description: 'Schedule preventive maintenance, major shutdowns, and inspections around production windows',
      department: 'Service',
      icon: Calendar,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Shutdown-window scheduling',
        'Multi-asset service coordination',
        'Planned maintenance reminders',
        'Reduce scheduling calls'
      ]
    },
    {
      id: 'safety-intake',
      name: 'Safety & Incident Intake',
      description: 'Capture safety incidents, near-misses, and hazard reports with structured data and auto-escalation',
      department: 'HSEQ',
      icon: HardHat,
      playbookIds: ['lead-capture'],
      benefits: [
        '24/7 incident reporting',
        'Structured data capture',
        'Automatic escalation',
        'Compliance documentation'
      ]
    },
    {
      id: 'contractor-coordination',
      name: 'Shift & Contractor Coordination',
      description: 'Handle contractor callouts, shift swaps, and site access enquiries across multiple crews',
      department: 'Operations',
      icon: Users,
      playbookIds: ['lead-capture', 'appointment-scheduler'],
      benefits: [
        'Contractor callout dispatch',
        'Site induction enquiries',
        'Shift coverage coordination',
        'Reduce supervisor interruptions'
      ]
    },
    {
      id: 'technical-enquiries',
      name: 'Technical & Specification Enquiries',
      description: 'Answer equipment, drill program, and consumable specification questions around the clock',
      department: 'Technical Support',
      icon: ClipboardList,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Instant spec lookups',
        'Drill program guidance',
        'Compatibility checks',
        'Documentation delivery'
      ]
    }
  ],

  challenges: [
    {
      title: 'Unplanned Downtime on Critical Assets',
      description: 'A single haul truck, shovel, or mill failure can cost tens of thousands per hour in lost production',
      impact: 'high',
      affectedAreas: ['Production output', 'Revenue impact', 'Contract penalties']
    },
    {
      title: 'Remote Sites, Limited Coverage',
      description: 'Mines often operate in remote regions with thin after hours support and long response windows',
      impact: 'high',
      affectedAreas: ['Response time', 'Crew safety', 'Operational continuity']
    },
    {
      title: 'Complex Parts & Consumables Sourcing',
      description: 'Thousands of SKUs across drill bits, liners, belts, picks, and OEM parts make accurate ordering hard',
      impact: 'high',
      affectedAreas: ['Order accuracy', 'Inventory costs', 'Downtime risk']
    },
    {
      title: 'Safety Reporting Friction',
      description: 'Incidents and near-misses go unreported when crews can\'t get through to the right person quickly',
      impact: 'high',
      affectedAreas: ['Regulatory compliance', 'Safety culture', 'Incident prevention']
    }
  ],

  solutions: [
    {
      challenge: 'Unplanned equipment downtime',
      solution: 'Deploy 24/7 breakdown triage Voice Genie with severity assessment and dispatch routing',
      implementation: 'Configure asset catalogue, severity matrix, and automatic notification to maintenance planners',
      expectedResults: [
        'Faster initial response on critical assets',
        'Better prioritisation across concurrent faults',
        'Less production time lost'
      ]
    },
    {
      challenge: 'Parts and consumables identification',
      solution: 'Voice-guided parts lookup cross-referencing by equipment model, serial, and description',
      implementation: 'Integrate with parts catalogue and inventory system for live stock and ordering',
      expectedResults: [
        'Lower return rates from mis-ordered parts',
        '24/7 ordering for remote sites',
        'Reduced buyer-desk call volume'
      ]
    },
    {
      challenge: 'Safety and near-miss capture',
      solution: 'Structured intake Genie that logs incidents, tags equipment, and escalates to HSEQ leads',
      implementation: 'Configure incident categories, lockout flags, and safety-manager notifications',
      expectedResults: [
        'Higher near-miss reporting rates',
        'Faster hazard containment',
        'Audit-ready compliance records'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'Pronto Xi',
        category: 'ERP',
        description: 'Asset-intensive ERP commonly used across mining operations',
        required: false,
        popularityScore: 78
      },
      {
        name: 'Pulse (Komatsu)',
        category: 'OEM Telemetry',
        description: 'OEM equipment health and fleet telemetry platform',
        required: false,
        popularityScore: 72
      },
      {
        name: 'MineStar',
        category: 'Fleet Management',
        description: 'Mining fleet management and dispatch platform',
        required: false,
        popularityScore: 75
      },
      {
        name: 'Pegasys / INX',
        category: 'HSEQ',
        description: 'Safety, health, and compliance management',
        required: false,
        popularityScore: 70
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Enterprise CRM platform',
        required: false,
        popularityScore: 95
      },
      {
        name: 'SAP',
        category: 'ERP',
        description: 'Enterprise resource planning',
        required: false,
        popularityScore: 92
      }
    ]
  },

  metrics: {
    averageImplementationTime: '2-4 weeks',
    typicalROI: '380%',
    adoptionRate: '90% site coverage',
    customerSatisfaction: '4.7/5'
  },

  demoConfig: {
    firstMessage: "Hi, thanks for calling. I can help with breakdowns, parts and consumables, safety reports, or shift and contractor coordination. What's happening on site?",
    systemPrompt: "You are a knowledgeable mining operations assistant. Prioritise safety and breakdowns, and quickly assess severity. Help callers with parts and consumables identification, service scheduling, safety intake, and contractor coordination. Be familiar with mining equipment, drill and blast terminology, and site safety protocols.",
    marketContext: "Mining operations and services company providing 24/7 support across remote sites",
    exampleScenarios: [
      "Haul truck just went down on the pit ramp",
      "Need to reorder drill bits for the RC rig",
      "Reporting a rockfall near-miss on the north wall",
      "Book a crusher liner change during the next shutdown"
    ]
  },

  caseStudies: [
    {
      companyName: 'Summit Mining Services',
      companySize: '8 sites across 3 regions',
      location: 'Asia-Pacific',
      challenge: 'Missed after hours breakdown calls from remote sites and inconsistent safety intake across contractors',
      implementation: 'Deployed Help Genie for breakdown triage, parts ordering, and safety intake across all sites and contractors',
      timeline: '3 weeks',
      results: [
        {
          metric: 'Breakdown Response Time',
          value: '-60%',
          improvement: 'Faster initial dispatch'
        },
        {
          metric: 'After Hours Coverage',
          value: '100%',
          improvement: 'Every site, every shift'
        },
        {
          metric: 'Near-Miss Reporting',
          value: '+42%',
          improvement: 'Higher capture rate'
        },
        {
          metric: 'Parts Order Accuracy',
          value: '+25%',
          improvement: 'Fewer returns'
        }
      ],
      quote: {
        text: "Our sites run around the clock and our phones have to keep up. Help Genie means a driller at 3 AM gets the same response as a day-shift supervisor — and nothing falls through the cracks.",
        author: 'Sarah Donovan',
        title: 'General Manager, Operations'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Mining Voice Genie Playbook',
      description: 'Complete guide to deploying voice genies across mine operations and services',
      url: '/resources/mining-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Cutting Unplanned Downtime with Voice Technology',
      description: 'How mining operators improve breakdown response times across remote sites',
      url: '/webinars/mining-support'
    },
    {
      type: 'template',
      title: 'Breakdown & Safety Intake Scripts',
      description: 'Pre-built voice scripts for mine-site emergencies and incident reporting',
      url: '/templates/mining-intake'
    }
  ],

  faqs: [
    {
      question: 'Can the genie handle remote sites with poor phone reception?',
      answer: 'Yes. Genies pick up calls that route through your existing line, so whether crews call from site satellite, UHF patch-through, or mobile, the intake is consistent. Structured data capture means partial calls still produce actionable records.'
    },
    {
      question: 'Does it work for contractors as well as direct employees?',
      answer: 'Absolutely. Many mining customers deploy genies specifically to handle contractor callouts, induction enquiries, and cross-crew coordination — where the volume is highest and the supervisors are stretched thin.'
    },
    {
      question: 'How does it integrate with our maintenance and HSEQ systems?',
      answer: 'Help Genie integrates with leading ERP, CMMS, and HSEQ platforms including SAP, Pronto, MineStar, and INX. Breakdown tickets, parts orders, and incident records flow straight into your existing workflow.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "A haul truck goes down at 3 AM\nyour Genie is already on it",
      subtitle: "See how your Genie handles real mine-site calls — triaging breakdowns, sourcing consumables, capturing safety reports, and coordinating contractors so the site keeps moving.",
    },
    chatScenarios: [
      {
        id: 'haul_truck_breakdown',
        title: 'Haul Truck Breakdown',
        messages: [
          { type: 'user' as const, text: "Haul truck 304 just went down on the ramp out of the pit. Blocking the cycle. Oil pressure light came on and the engine shut itself off.", delay: 300 },
          { type: 'genie' as const, text: "Understood — 304 blocking the cycle is critical. Logging this as production-stopping. Is the truck in a safe position with wheels chocked, and is the driver clear of the machine?", delay: 1200 },
          { type: 'user' as const, text: "Yep, it's chocked on the ramp, driver's stepped out. Needs to be cleared before the next load comes through.", delay: 2500 },
          { type: 'genie' as const, text: "Good. Oil-pressure shutdown on that model is usually a failed sensor, a line fault, or low oil. I'm dispatching the mobile maintenance crew now with a diagnostic kit and a spare sensor. Who's the on-shift supervisor I should notify?", delay: 3400 },
          { type: 'user' as const, text: "Dave Patterson, on channel 2. Mobile's 0412 555 843.", delay: 4600 },
          { type: 'genie' as const, text: "Breakdown ticket BD-2026-0412 created for haul truck 304. Maintenance crew dispatched — ETA 25 minutes. Dave Patterson will get a call in 2 minutes with the tech and ETA. I'll flag dispatch to hold loads at the stockpile.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Production-stop severity confirmed', delay: 6500 },
          { id: '2', text: 'Safety status verified (chocked, driver clear)', delay: 6700 },
          { id: '3', text: 'Probable fault assessed (oil pressure)', delay: 6900 },
          { id: '4', text: 'Mobile crew dispatched (25 min ETA)', delay: 7100 },
          { id: '5', text: 'Shift supervisor notified directly', delay: 7300 },
        ],
      },
      {
        id: 'consumables_reorder',
        title: 'Consumables Reorder',
        messages: [
          { type: 'user' as const, text: "Need to reorder drill bits for the RC rig on the exploration pad. Don't have the part number handy but the rig's the Epiroc Mustang 5-L.", delay: 300 },
          { type: 'genie' as const, text: "No worries — I can work from the rig model. The Mustang 5-L runs either the 5-inch or 5.5-inch face-sampling bits. Which diameter are you currently running, and is it button or tricone?", delay: 1200 },
          { type: 'user' as const, text: "5-inch, button bit. Been getting around 180 metres per bit and we've got about 400 metres of program left.", delay: 2500 },
          { type: 'genie' as const, text: "5-inch button bits for the Mustang, 3 units gives you buffer for the 400 metres plus contingency. I've got 6 in the regional warehouse, standard 2-day delivery to site, or overnight if it's urgent. Which works?", delay: 3400 },
          { type: 'user' as const, text: "Standard's fine, program runs another 10 days. Account Summit Exploration, deliver to the Gulah site.", delay: 4600 },
          { type: 'genie' as const, text: "Order placed — 3 × 5-inch button bits, 2-day delivery to the Gulah site, charged to Summit Exploration. You'll get tracking within the hour and an SMS when the freight's on the truck.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Rig model used to identify compatible bits', delay: 6500 },
          { id: '2', text: 'Diameter and type confirmed', delay: 6700 },
          { id: '3', text: 'Program requirement calculated', delay: 6900 },
          { id: '4', text: 'Stock verified and delivery scheduled', delay: 7100 },
          { id: '5', text: 'Site delivery and account coded', delay: 7300 },
        ],
      },
      {
        id: 'shutdown_scheduling',
        title: 'Shutdown Scheduling',
        messages: [
          { type: 'user' as const, text: "We need to book the crusher liner change and conveyor belt inspection for the September shutdown. Window is September 14–18.", delay: 300 },
          { type: 'genie' as const, text: "Good lead time. For the crusher liner change plus belt inspection on your primary plant, I'm booking a four-person crew for three days. Is the work scope the full liner set or just the worn wear zones?", delay: 1200 },
          { type: 'user' as const, text: "Full liner set this time, they're at 80% wear. And the belt inspection is on CV-01 and CV-02.", delay: 2500 },
          { type: 'genie' as const, text: "Full liner set plus two-conveyor inspection — that's a three-and-a-half day scope. I can book September 14–17, with the liner crew starting at 0600 on day one and the belt techs overlapping from day two. That leaves September 18 as buffer.", delay: 3400 },
          { type: 'user' as const, text: "That works. Shutdown supervisor is Marcus Liu, 0439 772 118. Site induction's still current.", delay: 4600 },
          { type: 'genie' as const, text: "Booked — crusher liner change plus belt inspection, September 14–17. Marcus Liu will get a confirmation with crew names, scope, and arrival times. I'll queue a one-week-out reminder and confirm liner stock 10 days prior.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Shutdown window matched to crew availability', delay: 6500 },
          { id: '2', text: 'Scope clarified (full liner + belts)', delay: 6700 },
          { id: '3', text: 'Crew size and sequence planned', delay: 6900 },
          { id: '4', text: 'Three-day booking confirmed with buffer', delay: 7100 },
          { id: '5', text: 'Confirmation and reminder queued', delay: 7300 },
        ],
      },
      {
        id: 'safety_near_miss',
        title: 'Safety Near-Miss',
        messages: [
          { type: 'user' as const, text: "Need to log a near-miss. We had a rockfall on the north wall, section N-12. About two cubic metres came down roughly ten metres from where a loader was operating.", delay: 300 },
          { type: 'genie' as const, text: "Serious one. First — is the loader operator uninjured, and has the area been cleared and barricaded?", delay: 1200 },
          { type: 'user' as const, text: "Operator's fine, nobody was hit. We've pulled the loader back and barricaded off a 30-metre exclusion zone.", delay: 2500 },
          { type: 'genie' as const, text: "Good response. Logging this as a safety near-miss — significant consequence potential. I need a few details: approximate time of the event, weather and ground conditions, and whether any scaling or monitoring was scheduled for that wall.", delay: 3400 },
          { type: 'user' as const, text: "Happened about 20 minutes ago, roughly 1:40 PM. Heavy rain overnight. Wall was last scaled 11 days ago and monitoring picked up some movement yesterday.", delay: 4600 },
          { type: 'genie' as const, text: "Near-miss report NM-2026-0214 filed. North wall N-12, 1:40 PM, post-rainfall event. I've tagged it as high-consequence given recent movement, notified the geotech lead and HSEQ manager, and flagged the wall for immediate re-assessment. Want me to book a scaling crew?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Safety status confirmed (no injuries)', delay: 6500 },
          { id: '2', text: 'Contextual details captured (weather, history)', delay: 6700 },
          { id: '3', text: 'Near-miss filed (NM-2026-0214)', delay: 6900 },
          { id: '4', text: 'Geotech and HSEQ leads notified', delay: 7100 },
          { id: '5', text: 'Follow-up action proposed', delay: 7300 },
        ],
      },
      {
        id: 'after_hours_remote',
        title: 'After Hours Remote',
        messages: [
          { type: 'user' as const, text: "Night shift, 2 AM. The dewatering pump at the north sump just tripped offline and water's rising. Pump's a Warman 6/4 AH.", delay: 300 },
          { type: 'genie' as const, text: "Dewatering trip on night shift — that needs fast eyes on it. On the Warman 6/4 AH, let's check the basics. Can you see the control panel? Is there an overload trip, and is the pit inlet screen visible?", delay: 1200 },
          { type: 'user' as const, text: "Overload's tripped. Can't see the inlet — it's under water now.", delay: 2500 },
          { type: 'genie' as const, text: "Classic blockage symptom on that pump. Overload trips when the inlet chokes on debris. Reset isn't safe until the inlet's cleared, and with the pit flooding you shouldn't send anyone in. I'm dispatching the on call maintenance crew with a backup pump. What's the water rise rate?", delay: 3400 },
          { type: 'user' as const, text: "Rising about 200 mm an hour, probably have six hours before it hits the haul road.", delay: 4600 },
          { type: 'genie' as const, text: "OK — on call crew dispatched with a portable 6/4 and containment gear, ETA 90 minutes. That puts a backup pump online well before the haul road risk. I'm also raising a geotech flag in case wall stability's affected. You'll get a call from the crew as soon as they're rolling.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Fault diagnosed remotely (overload trip)', delay: 6500 },
          { id: '2', text: 'Risk escalation rate quantified', delay: 6700 },
          { id: '3', text: 'Safe-work guidance given (no entry)', delay: 6900 },
          { id: '4', text: 'Backup pump dispatched (90 min ETA)', delay: 7100 },
          { id: '5', text: 'Secondary risk flagged (geotech)', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Keep the Site Running, Every Shift",
      subtitle: "When a haul truck goes down at 3 AM or a drill program runs out of bits, your Genie responds instantly — triaging, sourcing, and dispatching so production keeps moving.",
    },
    useCaseCards: [
      {
        id: 'breakdown-triage',
        title: 'Breakdown & Downtime Response',
        description: "Critical asset failures cost tens of thousands per hour. Your Genie assesses severity, confirms site safety, and dispatches crews and parts — in minutes, not hours.",
        bullets: [
          'Severity-based prioritisation',
          'Safety-first intake on every call',
          'Mobile maintenance dispatch with parts',
          'Faster response on production-stopping faults',
        ],
        image: '/solutions-pages/mining/usecase-breakdown-triage.webp',
      },
      {
        id: 'consumables-ordering',
        title: 'Parts & Consumables Sourcing',
        description: "No part number? Your Genie works from the rig or asset, cross-references, confirms stock, and places the order — with site delivery coordinated for the next freight run.",
        bullets: [
          'Cross-reference by equipment model',
          'Real-time stock availability',
          'Overnight and expedited delivery',
          'Account-based pricing applied',
        ],
        image: '/solutions-pages/mining/usecase-consumables-ordering.webp',
      },
      {
        id: 'shutdown-planning',
        title: 'Service & Shutdown Scheduling',
        description: "Liner changes, belt inspections, pump rebuilds — your Genie books service work around production windows and sends reminders so scheduled shutdowns stay on track.",
        bullets: [
          'Shutdown-window scheduling',
          'Multi-asset service coordination',
          'Automated PM reminders',
          'Compliance documentation tracked',
        ],
        image: '/solutions-pages/mining/usecase-shutdown-planning.webp',
      },
      {
        id: 'safety-intake',
        title: 'Safety & Incident Intake',
        description: "Near-misses and incidents captured the moment they happen — with structured data, equipment lockout flags, and automatic escalation to HSEQ leads.",
        bullets: [
          '24/7 incident reporting',
          'Structured data capture',
          'Equipment lockout flagging',
          'HSEQ manager auto-notification',
        ],
        image: '/solutions-pages/mining/usecase-safety-intake.webp',
      },
    ],
    finalCTA: {
      title: "Your Site Never Stops",
      subtitle: "Night shift breakdowns, remote sites, weekend consumable runs, contractor callouts — your Genie handles it all with the urgency and discipline mining operations demand.",
      pillars: [
        {
          title: 'Always\nResponding',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Breakdowns triaged and crews dispatched — at 3 AM or 3 PM.',
        },
        {
          title: 'Always\nSupplying',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Parts and consumables identified, stocked, and on the next freight.',
        },
        {
          title: 'Always\nProtecting',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Incidents captured, equipment locked out, HSEQ notified — instantly.',
        },
      ],
      backgroundImage: '/solutions-pages/mining/cta-background.webp',
    },
  }
};
